import { useContext } from 'react';
import { JourneyContext, useJourneyManager } from './hooks/useJourney';
import { LayoutContext } from './hooks/useLayoutContext';
import { useAppFlow } from './hooks/useAppFlow';
import { useIsEmbedded } from './hooks/useIsEmbedded';
import ChulakSession from './chulak/ChulakSession';
import { pages, resolveLayoutMode } from './pages';
import { isSingleApp } from './journey/appState';
import LandingPage from './pages/LandingPage';

/**
 * App — Provider shell. Creates the JourneyContext so all descendants
 * (including useAppFlow → useCancelConfirm) can consume it.
 */
function App() {
  // Embedded Ronan hands the result to the website's backend (webhook);
  // hosted Ronan redirects back to the client.
  const journey = useJourneyManager(useIsEmbedded() ? 'webhook' : 'redirect');

  return (
    <JourneyContext.Provider value={journey}>
      <AppContent />
    </JourneyContext.Provider>
  );
}

/**
 * AppContent — Renders inside JourneyContext.Provider, so hooks that
 * consume the journey (useCancelConfirm, useJourney) work safely.
 *
 * Layout differences are resolved once, up front, by picking a page set from
 * the `pages` map (see src/pages/index.ts). Everything below renders `Page.*`,
 * so the switch only cares about the flow state, not the platform.
 */
function AppContent() {
  const journey = useContext(JourneyContext)!;
  const {
    state,
    isMobile,
    isEmbedded,
    isPhone,
    layoutData,
    handleCancel,
    dispatch,
  } = useAppFlow(journey);

  const Page = pages[resolveLayoutMode({ isMobile, isEmbedded, isPhone })];
  const backToPicker = () => dispatch({ action: 'BACK_TO_PICKER' });

  const content = (() => {
    switch (state.type) {
      case 'appInstall':
        return <Page.AppInstall appName={state.appName} />;

      case 'landing':
        return <LandingPage />;

      case 'loading':
        return <Page.Loading />;

      case 'error':
        return (
          <Page.Abort
            variant={state.variant}
            onReject={state.canLeave ? () => journey.reject() : undefined}
            onRestart={() => window.location.reload()}
          />
        );

      case 'mobileFlowStart': {
        // Embedded mode never enters this state (treated as desktop in the
        // reducer), so this is always the hosted mobile page.
        const { session } = state;
        return (
          <pages.hostedMobile.FlowStart
            singleApp={isSingleApp(session)}
            onContinue={() => dispatch({ action: 'MOBILE_CONTINUE' })}
            onCancel={handleCancel}
          />
        );
      }

      case 'appSelect': {
        const { session } = state;
        return (
          <Page.AppPicker
            tokens={session.tokens}
            handlerApps={session.handlerApps}
            recommendedHandlerAppUuids={
              session.context.request.recommendedHandlerAppsUuids ?? []
            }
            onSelect={(token) => dispatch({ action: 'SELECT_TOKEN', token })}
            onCancel={handleCancel}
          />
        );
      }

      case 'chulak': {
        const { session, selectedToken } = state;
        return (
          <ChulakSession
            key={selectedToken.payload.body.handler}
            pages={Page}
            session={session}
            selectedToken={selectedToken}
            onBack={isSingleApp(session) ? undefined : backToPicker}
          />
        );
      }
    }
  })();

  return (
    <LayoutContext.Provider value={layoutData}>
      <Page.Shell>{content}</Page.Shell>
    </LayoutContext.Provider>
  );
}

export default App;
